<template>
  <div class="m-page">
    <div class="m-back">
      <el-button link type="primary" @click="$router.push(mPath('inventory'))">← 返回列表</el-button>
    </div>

    <div v-loading="loading" class="m-form-wrap">
      <template v-if="detail">
        <div class="m-summary">
          <div class="m-summary__code">{{ headerWarehouseCode }}<ColorCardMark :row="detail" /></div>
          <div v-if="systemIdentityCode" class="m-summary__sys muted">系统料号 {{ systemIdentityCode }}</div>
          <div class="m-summary__sub">{{ categoryLine }}</div>
          <div class="m-summary__loc muted">库位 {{ locationDisplayName }}</div>
        </div>

        <el-form label-position="top" class="m-form">
          <el-form-item label="库位">
            <div class="m-loc-row">
              <span class="m-loc-text">{{ locationDisplayName }}</span>
              <el-button type="primary" plain size="small" :disabled="!canAdjustLocation" @click="openLocDialog">调整库位</el-button>
            </div>
            <p class="m-field-hint">库位变更会生成「库存调拨」交易记录，不会直接改坐标。</p>
          </el-form-item>

          <el-form-item label="库存状态">
            <el-select v-model="form.inventory_status" placeholder="状态" style="width: 100%">
              <el-option label="可用" value="available" />
              <el-option label="预留" value="reserved" />
              <el-option label="在途" value="in_transit" />
              <el-option label="隔离" value="quarantine" />
              <el-option label="损坏" value="damaged" />
              <el-option label="报废" value="scrapped" />
              <el-option label="冻结" value="on_hold" />
            </el-select>
          </el-form-item>

          <el-form-item label="物理数量">
            <div class="m-qty-readonly">
              {{ fmtPhysicalOne(detail.physical_quantity) }}
              <MeterActualMark :row="detail" />
            </div>
            <div class="m-meter-actual-edit">
              <el-checkbox
                :model-value="!!detail?.physical_run_verified_at || !!detail?.mark_as_meter_actual"
                :disabled="!canOperateInventory || meterActualSaving"
                @change="onMeterActualChange"
              >
                跑码实数<span class="m-meter-actual-hint">（实）</span>
              </el-checkbox>
            </div>
            <div class="m-qty-actions">
              <el-button type="success" plain size="small" :disabled="!canOperateInventory || !canQtyAdjust" @click="openQtyDialog('overflow')">报溢入库</el-button>
              <el-button type="warning" plain size="small" :disabled="!canOperateInventory || !canQtyAdjust" @click="openQtyDialog('loss')">报损出库</el-button>
            </div>
            <p class="m-field-hint">数量增减请走报溢 / 报损，将写入库存交易并更新账面。跑码实数仅作标记，不改数量。</p>
          </el-form-item>

          <el-form-item label="预留数量">
            <el-input :model-value="fmtQty(detail.reserved_quantity)" readonly class="m-readonly-input" />
            <p class="m-field-hint">预留由订单配料占用汇总，此处不可改。</p>
          </el-form-item>

          <el-form-item label="仓库料号">
            <el-input :model-value="warehouseDisplay" readonly class="m-readonly-input" />
          </el-form-item>

          <el-form-item label="存储备注">
            <el-input v-model="form.storage_notes" type="textarea" :rows="2" placeholder="与什么放一起等" />
            <div class="m-color-card-edit">
              <el-checkbox
                :model-value="!!detail?.has_color_card"
                :disabled="!canOperateInventory || colorCardSaving"
                @change="onColorCardChange"
              >
                有色卡<span class="m-color-card-hint">（色卡）</span>
              </el-checkbox>
            </div>
          </el-form-item>
          <el-form-item label="品质备注">
            <el-input v-model="form.quality_notes" type="textarea" :rows="2" placeholder="瑕疵、偏黄等" />
          </el-form-item>

          <el-form-item label="启用">
            <el-switch v-model="form.is_active" active-text="是" inactive-text="否" />
          </el-form-item>

          <el-form-item v-if="detail.material_batch" label="分支格子（库存里维护）">
            <p class="m-field-hint">
              在「材料库存管理」本页即可维护；查看与录入分开。空格子表示无货，保存后写入该料号的分支明细，合计须等于上方物理数量。
            </p>
            <div class="m-branch-bar">
              <el-button type="primary" plain size="small" @click="branchViewOpen = true">查看分支格子</el-button>
              <el-button v-if="canEditMaterialBatch" type="success" plain size="small" @click="branchEditOpen = true">
                录入分支格子
              </el-button>
            </div>
            <p v-if="!canEditMaterialBatch" class="m-field-hint">录入需管理员授予「料号编辑」权限。</p>
          </el-form-item>
        </el-form>

        <div class="m-actions m-actions--split">
          <el-button v-if="canViewSlotTransactions" type="default" size="large" @click="openTxDrawer">料号交易记录</el-button>
          <el-button type="primary" size="large" :disabled="!canOperateInventory" :loading="saving" @click="save">保存修改</el-button>
        </div>

        <p class="m-hint">此处保存仅更新状态与备注；数量与库位请使用上方专用入口。</p>
        <p v-if="!canViewSlotTransactions" class="m-hint">
          无料号交易查看权限。请管理员在权限中心勾选「移动端料号交易-脱敏查看」或「完整查看」。
        </p>
        <p v-else-if="!canViewSlotTransactionsFull" class="m-hint">
          当前为脱敏查看：不显示客户 / 供应商名称。
        </p>

        <el-dialog v-model="locDialogVisible" title="调整库位（移库）" width="90%" @closed="resetLocForm">
          <el-form label-position="top">
            <el-form-item label="当前库位">
              <el-input :model-value="locationDisplayName" readonly />
            </el-form-item>
            <el-form-item label="账面数量">
              <el-input :model-value="fmtPhysicalOne(detail?.physical_quantity)" readonly />
            </el-form-item>
            <el-form-item label="移库数量" required>
              <el-input-number
                v-model="locForm.transfer_quantity"
                :min="qtyInputMin"
                :max="locTransferMaxQty"
                :precision="qtyPrecision"
                :step="qtyBumpStep"
                controls-position="right"
                style="width: 100%"
              />
              <p class="m-field-hint">默认移动本库位<strong>全部可移库</strong>数量；若只移一部分，请改小此处。</p>
              <p v-if="isInventoryAdmin" class="m-field-hint">管理员可输入最多 4 位小数以调平尾差。</p>
              <p v-if="detail?.transfer_unlocked_before_dispatch" class="m-field-hint">
                派单分码前：可按料号整批移库，不按库位锁定配料占位。
              </p>
              <p v-else-if="detail?.needs_location_confirmation" class="m-field-hint">
                当前为未确定位置；确认实库位后，派单将带出最新库位。
              </p>
              <p v-else-if="detail?.transfer_limited_by_reservation && locTransferMaxQty + 1e-6 < Number(detail?.physical_quantity || 0)" class="m-field-hint">
                派单分码后存在配料/派工占用，可移库量 {{ fmtPhysicalOne(locTransferMaxQty) }}，小于账面数量。
              </p>
            </el-form-item>
            <el-form-item label="新库位" required>
              <el-select
                v-model="locForm.to_location_id"
                filterable
                placeholder="输入编码或名称搜索，如 R24"
                style="width: 100%"
              >
                <el-option
                  v-for="loc in locations"
                  :key="loc.location_id"
                  :label="locationOptionLabel(loc)"
                  :value="loc.location_id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="locForm.notes" type="textarea" :rows="2" placeholder="选填" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="locDialogVisible = false">取消</el-button>
            <el-button type="primary" :disabled="!canAdjustLocation" :loading="locSubmitting" @click="submitLocTransfer">确认调整</el-button>
          </template>
        </el-dialog>

        <el-dialog v-model="qtyDialogVisible" :title="qtyDialogTitle" width="90%" @closed="resetQtyForm">
          <p class="m-qty-cur">当前物理数量 <strong>{{ fmtPhysicalOne(detail?.physical_quantity) }}</strong></p>
          <p class="m-qty-tip">
            {{
              qtyMode === 'overflow'
                ? '填「增加数量」按该数额报溢；只填「实际数量」则自动按与当前的差额报溢。两者都填时以「增加数量」为准。'
                : '填「减少数量」按该数额报损；只填「实际数量」则自动按与当前的差额报损。两者都填时以「减少数量」为准。'
            }}
          </p>
          <el-form label-position="top" class="m-qty-form">
            <template v-if="qtyMode === 'overflow'">
              <el-form-item label="增加数量">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.deltaInput"
                    inputmode="decimal"
                    placeholder="留空可仅填下行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('delta')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('delta', qtyBumpStep)">+{{ qtyBumpStep }}</el-button>
                    <el-button size="small" @click="bumpQtyField('delta', -qtyBumpStep)">−{{ qtyBumpStep }}</el-button>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="实际数量（报溢后）">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.actualInput"
                    inputmode="decimal"
                    placeholder="留空可仅填上行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('actual')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('actual', qtyBumpStep)">+{{ qtyBumpStep }}</el-button>
                    <el-button size="small" @click="bumpQtyField('actual', -qtyBumpStep)">−{{ qtyBumpStep }}</el-button>
                  </div>
                </div>
              </el-form-item>
            </template>
            <template v-else>
              <el-form-item label="减少数量">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.deltaInput"
                    inputmode="decimal"
                    placeholder="留空可仅填下行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('delta')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('delta', qtyBumpStep)">+{{ qtyBumpStep }}</el-button>
                    <el-button size="small" @click="bumpQtyField('delta', -qtyBumpStep)">−{{ qtyBumpStep }}</el-button>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="实际数量（报损后）">
                <div class="m-qty-row">
                  <el-input
                    v-model="qtyForm.actualInput"
                    inputmode="decimal"
                    placeholder="留空可仅填上行"
                    clearable
                    class="m-qty-plain"
                    @blur="onBlurQtyField('actual')"
                  />
                  <div class="m-qty-step">
                    <el-button size="small" @click="bumpQtyField('actual', qtyBumpStep)">+{{ qtyBumpStep }}</el-button>
                    <el-button size="small" @click="bumpQtyField('actual', -qtyBumpStep)">−{{ qtyBumpStep }}</el-button>
                  </div>
                </div>
              </el-form-item>
            </template>
            <el-form-item label="备注">
              <el-input v-model="qtyForm.notes" type="textarea" :rows="2" placeholder="选填" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="qtyDialogVisible = false">取消</el-button>
            <el-button type="primary" :disabled="!canOperateInventory" :loading="qtySubmitting" @click="submitQtyTransaction">提交</el-button>
          </template>
        </el-dialog>

        <MobileBatchBranchViewDrawer
          v-model="branchViewOpen"
          :branch-details="branchViewMergedDetails"
          :physical-qty="detail.physical_quantity"
          :enable-branch="!!detail.material_batch?.enable_branch_management"
        />
        <MobileBatchBranchEditDrawer
          v-model="branchEditOpen"
          :inventory-id="detail.inventory_id"
          :batch-id="detail.material_batch?.batch_id"
          :initial-branch-details="branchEditInitialDetails"
          :physical-qty="detail.physical_quantity"
          @saved="onBranchSavedFromDrawer"
        />

        <el-drawer v-model="txDrawerVisible" title="料号交易记录" direction="btt" size="70%">
          <div v-loading="txLoading" class="m-tx-drawer">
            <div v-for="tx in txRows" :key="tx.transaction_id" class="m-tx-card">
              <div class="m-tx-card__head">
                <span>{{ tx.transaction_number || '#' + tx.transaction_id }}</span>
                <span class="muted">{{ fmtTxDate(tx.transaction_date) }}</span>
              </div>
              <div class="m-tx-line">
                类型 {{ tx.transaction_type?.type_name || tx.transaction_type_id }}
                · 变动数量 {{ formatInventoryTxChangeQty(tx) }}
              </div>
              <div v-if="formatInventoryTxLocationChange(tx)" class="m-tx-line">
                {{ formatInventoryTxLocationChange(tx) }}
              </div>
              <div v-if="formatInventoryTxLocationBalances(tx).length" class="m-tx-balances">
                <div class="m-tx-line">当下库存</div>
                <div
                  v-for="(line, bi) in formatInventoryTxLocationBalances(tx)"
                  :key="bi"
                  class="m-tx-line m-tx-balance-line"
                >
                  {{ line }}
                </div>
              </div>
              <div v-if="txOutboundLabel(tx)" class="m-tx-line">出库对象 {{ txOutboundLabel(tx) }}</div>
              <div v-if="txOrderContext(tx)" class="m-tx-line muted">{{ txOrderContext(tx) }}</div>
              <div v-else-if="txSupplier(tx)" class="m-tx-line muted">{{ txSupplier(tx) }}</div>
              <div
                v-if="
                  canViewSlotTransactionsFull &&
                  shouldShowInventoryTxNotes(tx) &&
                  (formatNotesWithCounterparty(tx) || tx.notes)
                "
                class="m-tx-line muted"
              >
                {{ formatNotesWithCounterparty(tx) || tx.notes }}
              </div>
            </div>
            <el-empty v-if="!txLoading && txRows.length === 0" description="暂无记录" />
            <div v-if="txPages > 1" class="m-tx-pager">
              <el-button size="small" :disabled="txPage <= 1" @click="txPrev">上一页</el-button>
              <span>{{ txPage }} / {{ txPages }}</span>
              <el-button size="small" :disabled="txPage >= txPages" @click="txNext">下一页</el-button>
            </div>
          </div>
        </el-drawer>
      </template>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'MobileInventoryDetail' })
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useMobileWorkbench } from '@/composables/useMobileWorkbench'

const { mPath, mRouteName } = useMobileWorkbench()
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getMaterialInventoryRecord,
  updateMaterialInventory,
  getMaterialInventorySlotTransactions,
  transferMaterialInventoryLocation,
  patchMaterialInventoryColorCardMark,
  patchMaterialInventoryMeterActualMark
} from '@/api/materialInventory'
import ColorCardMark from '@/components/ColorCardMark.vue'
import MeterActualMark from '@/components/MeterActualMark.vue'
import { createInventoryTransaction } from '@/api/inventoryTransactions'
import {
  interpretInventoryTransactionResponse,
  notifyInventoryTransactionResult
} from '@/utils/inventoryTransactionResponse'
import { fetchAllActiveStorageLocations } from '@/utils/fetchAllStorageLocations'
import { getUnits } from '@/api/units'
import { getInventoryTransactionTypes } from '@/api/inventoryTransactionTypes'
import { useUserStore } from '@/store/user'
import { canEditInventoryRecords, hasPermission } from '@/utils/permission'
import MobileBatchBranchViewDrawer from '@/views/mobile/components/MobileBatchBranchViewDrawer.vue'
import MobileBatchBranchEditDrawer from '@/views/mobile/components/MobileBatchBranchEditDrawer.vue'
import { mergedBranchDetailsForDisplay } from '@/utils/materialBatchBranchGrid'
import {
  formatInventoryTxOutboundObject,
  formatInventoryTxOrderContext,
  formatInventoryTxSupplier,
  formatInventoryTxChangeQty,
  formatInventoryTxLocationChange,
  formatInventoryTxLocationBalances,
  shouldShowInventoryTxNotes,
  isDispatchCompleteTransitRow
} from '@/utils/inventoryTransactionDisplay'
import {
  inventoryQtyInputDecimals,
  inventoryQtyInputStep,
  inventoryQtyInputMin,
  inventoryQtyInputPrecision,
  parseQtyInputToNumber as parseQtyByDecimals,
  formatQtyInputString as formatQtyByDecimals,
  roundInventoryQty,
  bumpQtyInputValue
} from '@/utils/inventoryQtyInput'
import { formatNotesWithCounterparty } from '@/views/inventory/InventoryTransactions/utils/formatters'

const route = useRoute()
const userStore = useUserStore()
const canOperateInventory = computed(
  () =>
    hasPermission(userStore.user, 'inventory.operations.edit') ||
    canEditInventoryRecords(userStore.user)
)
const canEditMaterialBatch = computed(() => hasPermission(userStore.user, 'inventory.material_batches.edit'))
const isInventoryAdmin = computed(() => !!(userStore.isAdmin || userStore.isSuperuser))
const qtyDecimals = computed(() => inventoryQtyInputDecimals(userStore))
const qtyBumpStep = computed(() => inventoryQtyInputStep(userStore))
const qtyPrecision = computed(() => inventoryQtyInputPrecision(userStore))
const qtyInputMin = computed(() => inventoryQtyInputMin(userStore))
const canAdjustLocation = computed(() => {
  if (!canOperateInventory.value) return false
  if (!isDispatchCompleteTransitRow(detail.value)) return true
  return isInventoryAdmin.value
})

const branchViewOpen = ref(false)
const branchEditOpen = ref(false)

/** 查看：与列表 API 一致，优先本库位 slot 合并后的 branch_details */
const branchViewMergedDetails = computed(() => mergedBranchDetailsForDisplay(detail.value))
/** 录入预填：本库位已保存优先，勿只用料号全局 */
const branchEditInitialDetails = computed(() => {
  const d = detail.value
  if (!d) return null
  if (d.slot_branch_details != null) return d.slot_branch_details
  return d.branch_details ?? d.material_batch?.branch_details ?? null
})
const loading = ref(true)
const saving = ref(false)
const colorCardSaving = ref(false)
const meterActualSaving = ref(false)
const detail = ref(null)
const locations = ref([])

const overflowTypeId = ref(null)
const lossTypeId = ref(null)
const yardUnitId = ref(null)

const form = reactive({
  inventory_status: 'available',
  storage_notes: '',
  quality_notes: '',
  is_active: true
})

const locDialogVisible = ref(false)
const locSubmitting = ref(false)
const locForm = reactive({
  to_location_id: null,
  transfer_quantity: null,
  notes: ''
})

const locTransferMaxQty = computed(() => {
  const d = detail.value
  if (!d) return 0
  const xfer = Number(d.location_transferable_quantity)
  if (Number.isFinite(xfer) && xfer > 0) return xfer
  const phys = Number(d.physical_quantity)
  return Number.isFinite(phys) && phys > 0 ? phys : 0
})

const qtyDialogVisible = ref(false)
const qtyMode = ref('overflow')
const qtySubmitting = ref(false)
const qtyForm = reactive({
  /** 增加/减少数量，字符串便于留空、少显示尾随 0 */
  deltaInput: '',
  actualInput: '',
  notes: ''
})

const warehouseDisplay = computed(() => {
  const w = detail.value?.warehouse_manual_code
  return w != null && String(w).trim() !== '' ? String(w).trim() : '—'
})

const systemIdentityCode = computed(() => {
  const c = detail.value?.material_identity?.material_identity_code
  return c != null && String(c).trim() !== '' ? String(c).trim() : ''
})

/** 台头优先仓库料号（现场主用）；无则退回系统料号 */
const headerWarehouseCode = computed(() => {
  const w = warehouseDisplay.value
  if (w && w !== '—') return w
  return systemIdentityCode.value || '—'
})

const categoryLine = computed(() => {
  const mi = detail.value?.material_identity
  if (!mi) return ''
  const cat = mi.material_category?.internal_name || ''
  const colorParts = [mi.color?.internal_name_cn, mi.color?.internal_name_en].filter(Boolean)
  const col = colorParts.join(' / ')
  return [cat, col].filter(Boolean).join(' · ')
})

const locationDisplayName = computed(() => {
  const loc = detail.value?.storage_location
  if (!loc) return '—'
  return (loc.location_name || loc.location_code || '—').trim()
})

const canViewSlotTransactions = computed(() => {
  if (detail.value?.can_view_slot_transactions != null) {
    return !!detail.value.can_view_slot_transactions
  }
  return userStore.canViewInventorySlotTransactions
})

const canViewSlotTransactionsFull = computed(() => {
  if (detail.value?.can_view_slot_transactions_full != null) {
    return !!detail.value.can_view_slot_transactions_full
  }
  if (detail.value?.mobile_tx_privacy === 'full') return true
  if (detail.value?.mobile_tx_privacy === 'masked') return false
  return userStore.canViewInventorySlotTransactionsFull
})

const txMaskOpts = computed(() =>
  canViewSlotTransactionsFull.value ? {} : { maskCounterparty: true }
)

const canQtyAdjust = computed(() => {
  return !!(overflowTypeId.value && lossTypeId.value && yardUnitId.value && detail.value?.material_batch_id && detail.value?.storage_location_id)
})

const qtyDialogTitle = computed(() => (qtyMode.value === 'overflow' ? '报溢入库' : '报损出库'))

const txDrawerVisible = ref(false)
const txLoading = ref(false)
const txRows = ref([])
const txPage = ref(1)
const txPages = ref(1)

function fmtQty(v) {
  if (v == null || v === '') return '0'
  const n = Number(v)
  return Number.isFinite(n) ? n.toLocaleString('zh-CN', { maximumFractionDigits: 4 }) : String(v)
}

/** 物理数量展示：管理员最多 4 位，其它最多 1 位 */
function fmtPhysicalOne(v) {
  if (v == null || v === '') return '0'
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  const d = qtyDecimals.value
  const r = roundInventoryQty(n, d)
  if (r == null) return String(v)
  if (Number.isInteger(r)) return String(r)
  return String(parseFloat(r.toFixed(d)))
}

function parseQtyInputToNumber(str) {
  return parseQtyByDecimals(str, qtyDecimals.value)
}

/** 按当前账号精度写入输入框 */
function formatQtyInputString(n) {
  return formatQtyByDecimals(n, qtyDecimals.value)
}

function onBlurQtyField(which) {
  const key = which === 'delta' ? 'deltaInput' : 'actualInput'
  const n = parseQtyInputToNumber(qtyForm[key])
  qtyForm[key] = n == null ? '' : formatQtyInputString(n)
}

function bumpQtyField(which, step) {
  const key = which === 'delta' ? 'deltaInput' : 'actualInput'
  const cur = parseQtyInputToNumber(qtyForm[key])
  const next = bumpQtyInputValue(cur, step, qtyDecimals.value)
  qtyForm[key] = next == null || next <= 0 ? '' : formatQtyInputString(next)
}

function fmtTxDate(s) {
  if (!s) return '—'
  return String(s).replace('T', ' ').slice(0, 19)
}

function txOutboundLabel(tx) {
  return formatInventoryTxOutboundObject(tx, txMaskOpts.value)
}

function txOrderContext(tx) {
  return formatInventoryTxOrderContext(tx)
}

function txSupplier(tx) {
  return formatInventoryTxSupplier(tx, txMaskOpts.value)
}

async function loadMeta() {
  try {
    const [typesRes, unitsRes] = await Promise.all([
      getInventoryTransactionTypes({ page: 1, per_page: 200, is_active: true }),
      getUnits({ page: 1, per_page: 500, is_active: true })
    ])
    const types = (typesRes && typesRes.data) || []
    const overflow = types.find((t) => t.type_code === 'OVERFLOW_IN')
    const loss = types.find((t) => t.type_code === 'LOSS_OUT')
    overflowTypeId.value = overflow?.type_id ?? null
    lossTypeId.value = loss?.type_id ?? null

    const units = (unitsRes && unitsRes.data) || []
    const yard =
      units.find((u) => u.unit_code === 'yd' || u.unit_code === 'YD' || u.unit_code === 'YARD') ||
      units.find((u) => u.unit_name === '码')
    yardUnitId.value = yard?.unit_id ?? null

    if (!overflowTypeId.value || !lossTypeId.value) {
      console.warn('未找到报溢/报损交易类型 OVERFLOW_IN / LOSS_OUT')
    }
    if (!yardUnitId.value) {
      console.warn('未找到默认单位「码」')
    }
  } catch (e) {
    console.error(e)
  }
}

async function loadTxPage(page) {
  const id = Number(route.params.id)
  if (!id) return
  txLoading.value = true
  txPage.value = page
  try {
    const res = await getMaterialInventorySlotTransactions(id, { page, per_page: 20 })
    if (res.success) {
      txRows.value = res.data || []
      const p = res.pagination || {}
      txPages.value = p.pages || 1
      txPage.value = p.page || page
    } else {
      ElMessage.error(res.message || '加载失败')
      txRows.value = []
    }
  } catch (e) {
    if (e.response?.status === 403) {
      ElMessage.warning(e.response?.data?.message || '无权限查看')
    } else {
      ElMessage.error(e.response?.data?.message || e.message || '加载失败')
    }
    txRows.value = []
  } finally {
    txLoading.value = false
  }
}

function openTxDrawer() {
  if (!canViewSlotTransactions.value) {
    ElMessage.warning('无权限查看库存交易记录')
    return
  }
  txDrawerVisible.value = true
  loadTxPage(1)
}

function txPrev() {
  if (txPage.value <= 1) return
  loadTxPage(txPage.value - 1)
}

function txNext() {
  if (txPage.value >= txPages.value) return
  loadTxPage(txPage.value + 1)
}

function applyDetail(d) {
  detail.value = d
  form.inventory_status = d.inventory_status || 'available'
  form.storage_notes = d.storage_notes || ''
  form.quality_notes = d.quality_notes || ''
  form.is_active = d.is_active !== false
}

async function onBranchSavedFromDrawer () {
  branchEditOpen.value = false
  const id = Number(route.params.id)
  if (!id) return
  try {
    const res = await getMaterialInventoryRecord(id)
    if (res.success && res.data) {
      applyDetail(res.data)
    }
  } catch (e) {
    console.error(e)
  }
}

function locationOptionLabel(loc) {
  if (!loc) return '—'
  const code = (loc.location_code || '').trim()
  const name = (loc.location_name || '').trim()
  if (code && name && code !== name) return `${code} - ${name}`
  return code || name || String(loc.location_id)
}

async function loadLocations() {
  try {
    locations.value = await fetchAllActiveStorageLocations()
  } catch (e) {
    console.error(e)
    locations.value = []
  }
}

function openLocDialog() {
  if (!canOperateInventory.value) {
    ElMessage.warning('您没有调整库位权限，请联系管理员')
    return
  }
  if (!canAdjustLocation.value) {
    ElMessage.warning('派工完成虚库仅管理员可移库；开送货单出库不受影响')
    return
  }
  locForm.to_location_id = null
  locForm.transfer_quantity = locTransferMaxQty.value > 0 ? locTransferMaxQty.value : null
  locForm.notes = ''
  locDialogVisible.value = true
}

function resetLocForm() {
  locForm.to_location_id = null
  locForm.transfer_quantity = null
  locForm.notes = ''
}

async function submitLocTransfer() {
  if (!canOperateInventory.value) {
    ElMessage.warning('您没有调整库位权限，请联系管理员')
    return
  }
  if (!canAdjustLocation.value) {
    ElMessage.warning('派工完成虚库仅管理员可移库；开送货单出库不受影响')
    return
  }
  const id = Number(route.params.id)
  if (!id) return
  if (!locForm.to_location_id) {
    ElMessage.warning('请选择新库位')
    return
  }
  const cur = detail.value?.storage_location_id ?? detail.value?.storage_location?.location_id
  if (locForm.to_location_id === cur) {
    ElMessage.warning('新库位与当前相同')
    return
  }
  const qty = Number(locForm.transfer_quantity)
  if (!qty || qty <= 0) {
    ElMessage.warning('请填写有效的移库数量')
    return
  }
  if (qty > locTransferMaxQty.value + 1e-6) {
    ElMessage.warning(`移库数量不能超过可移库量 ${fmtPhysicalOne(locTransferMaxQty.value)}`)
    return
  }
  locSubmitting.value = true
  try {
    const res = await transferMaterialInventoryLocation(id, {
      storage_location_id: locForm.to_location_id,
      quantity: qty,
      notes: locForm.notes || ''
    })
    if (res.success) {
      const moved = res.data?.moved_quantity
      ElMessage.success(
        moved != null
          ? res.data?.partial
            ? `已移库 ${fmtPhysicalOne(moved)}（部分移库，其余仍在原库位）`
            : `已移库 ${fmtPhysicalOne(moved)}`
          : res.message || '库位已调整'
      )
      locDialogVisible.value = false
      await load()
    } else {
      ElMessage.error(res.message || '调整失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '调整失败')
  } finally {
    locSubmitting.value = false
  }
}

async function openQtyDialog(mode) {
  if (!canOperateInventory.value) {
    ElMessage.warning('您没有库存调整权限，请联系管理员')
    return
  }
  await loadMeta()
  const need = mode === 'overflow' ? overflowTypeId.value : lossTypeId.value
  if (!need || !yardUnitId.value || !detail.value?.material_batch_id || !detail.value?.storage_location_id) {
    ElMessage.warning('缺少报溢/报损类型或单位配置，或本行无库位/批次，请联系管理员')
    return
  }
  qtyMode.value = mode
  qtyForm.deltaInput = ''
  qtyForm.actualInput = ''
  qtyForm.notes = ''
  qtyDialogVisible.value = true
}

function resetQtyForm() {
  qtyForm.deltaInput = ''
  qtyForm.actualInput = ''
  qtyForm.notes = ''
}

/** 报溢：返回交易数量（正数）或 { error } */
function resolveOverflowSubmitQty() {
  const cur = Number(detail.value?.physical_quantity) || 0
  const d = parseQtyInputToNumber(qtyForm.deltaInput)
  const a = parseQtyInputToNumber(qtyForm.actualInput)
  if (d != null && d > 0) return d
  if (a != null) {
    const inc = roundInventoryQty(a - cur, qtyDecimals.value)
    if (inc == null || inc <= 0) return { error: '实际数量须大于当前物理数量，差额才会作为报溢量' }
    return inc
  }
  return { error: '请填写「增加数量」或「实际数量」其一' }
}

/** 报损：返回交易数量（正数，表示出库扣减量）或 { error } */
function resolveLossSubmitQty() {
  const cur = Number(detail.value?.physical_quantity) || 0
  const d = parseQtyInputToNumber(qtyForm.deltaInput)
  const a = parseQtyInputToNumber(qtyForm.actualInput)
  if (d != null && d > 0) {
    if (d > cur) return { error: '减少数量不能大于当前物理数量' }
    return d
  }
  if (a != null) {
    if (a >= cur) return { error: '报损后的实际数量须小于当前物理数量' }
    if (a < 0) return { error: '实际数量不能为负' }
    const loss = roundInventoryQty(cur - a, qtyDecimals.value)
    if (loss == null || loss <= 0) return { error: '差额须大于 0 才能报损' }
    return loss
  }
  return { error: '请填写「减少数量」或「实际数量」其一' }
}

async function submitQtyTransaction() {
  if (!canOperateInventory.value) {
    ElMessage.warning('您没有库存调整权限，请联系管理员')
    return
  }
  const id = Number(route.params.id)
  if (!id) return
  const inv = detail.value
  const typeId = qtyMode.value === 'overflow' ? overflowTypeId.value : lossTypeId.value
  const resolved = qtyMode.value === 'overflow' ? resolveOverflowSubmitQty() : resolveLossSubmitQty()
  if (typeof resolved === 'object' && resolved?.error) {
    ElMessage.warning(resolved.error)
    return
  }
  const q = resolved
  if (!Number.isFinite(q) || q <= 0) {
    ElMessage.warning('请填写有效的数量')
    return
  }
  qtySubmitting.value = true
  try {
    const operator = userStore.user?.username || userStore.user?.name || '用户'
    const timeStr = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const noteParts = [`[操作员：${operator}] [时间：${timeStr}]`, qtyForm.notes].filter(Boolean)
    const res = await createInventoryTransaction({
      transaction_type_id: typeId,
      quantity: q,
      unit_id: yardUnitId.value,
      notes: noteParts.join(' '),
      inventory_id: inv.inventory_id,
      material_identity_id: inv.material_identity?.identity_id || inv.material_batch?.material_identity_id,
      material_batch_id: inv.material_batch_id || inv.material_batch?.batch_id,
      storage_location_id: inv.storage_location_id || inv.storage_location?.location_id,
      is_adjustment: true,
      related_order_type: 'adjustment'
    })
    const result = interpretInventoryTransactionResponse(res, '已提交审批')
    if (notifyInventoryTransactionResult(result)) {
      qtyDialogVisible.value = false
      if (!result.pendingApproval) await load()
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    qtySubmitting.value = false
  }
}

function isInventoryDetailRoute() {
  return route.name === mRouteName('MobileInventoryDetail')
}

async function load() {
  const id = Number(route.params.id)
  if (!isInventoryDetailRoute() || !id) {
    loading.value = false
    return
  }
  loading.value = true
  try {
    const res = await getMaterialInventoryRecord(id)
    if (res.success && res.data) {
      applyDetail(res.data)
    } else {
      ElMessage.error(res.message || '加载失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

async function onMeterActualChange(marked) {
  const invId = detail.value?.inventory_id
  if (!invId) {
    ElMessage.warning('该记录无库存明细 ID，无法标记跑码实数')
    return
  }
  if (!canOperateInventory.value) {
    ElMessage.warning('无库存操作权限')
    return
  }
  const next = !!marked
  try {
    await ElMessageBox.confirm(
      next
        ? '确认标记为「跑码实数」？物理数量旁将显示红字「（实）」。'
        : '确认取消「跑码实数」标记？数量旁将不再显示「（实）」。',
      next ? '标记跑码实数' : '取消跑码实数',
      { type: 'warning', confirmButtonText: '确认', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  meterActualSaving.value = true
  try {
    const res = await patchMaterialInventoryMeterActualMark(Number(invId), { marked: next })
    if (res?.success) {
      if (detail.value) {
        detail.value.physical_run_verified_at = next
          ? res.data?.physical_run_verified_at || new Date().toISOString()
          : null
        detail.value.mark_as_meter_actual = next
      }
      ElMessage.success(next ? '已标记跑码实数' : '已取消跑码实数')
    } else {
      ElMessage.error(res?.message || '标记失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '标记失败')
  } finally {
    meterActualSaving.value = false
  }
}

async function onColorCardChange(marked) {
  const invId = detail.value?.inventory_id
  if (!invId) {
    ElMessage.warning('该记录无库存明细 ID，无法标记色卡')
    return
  }
  if (!canOperateInventory.value) {
    ElMessage.warning('无库存操作权限')
    return
  }
  const next = !!marked
  try {
    await ElMessageBox.confirm(
      next
        ? '确认标记为「已裁料做色卡」？料号旁将显示红字「色卡」。'
        : '确认取消色卡标记？料号旁将不再显示「色卡」。',
      next ? '标记色卡' : '取消色卡',
      { type: 'warning', confirmButtonText: '确认', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  colorCardSaving.value = true
  try {
    const res = await patchMaterialInventoryColorCardMark(Number(invId), { marked: next })
    if (res?.success) {
      const flag = res.data?.has_color_card != null ? !!res.data.has_color_card : next
      if (detail.value) detail.value.has_color_card = flag
      ElMessage.success(flag ? '已标记色卡' : '已取消色卡')
    } else {
      ElMessage.error(res?.message || '标记失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '标记失败')
  } finally {
    colorCardSaving.value = false
  }
}

async function save() {
  if (!canOperateInventory.value) {
    ElMessage.warning('您没有保存库存信息权限，请联系管理员')
    return
  }
  const id = Number(route.params.id)
  saving.value = true
  try {
    const payload = {
      inventory_status: form.inventory_status,
      storage_notes: form.storage_notes || null,
      quality_notes: form.quality_notes || null,
      is_active: form.is_active
    }
    const res = await updateMaterialInventory(id, payload)
    if (res.success) {
      ElMessage.success('已保存')
      await load()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    saving.value = false
  }
}

watch(
  () => route.params.id,
  (id) => {
    if (!isInventoryDetailRoute() || !id) return
    load()
  }
)

onMounted(async () => {
  userStore.initFromStorage()
  await loadMeta()
  await loadLocations()
  await load()
})
</script>

<style scoped>
.m-page {
  max-width: 720px;
  margin: 0 auto;
}

.m-back {
  margin-bottom: 8px;
}

.m-summary {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.m-summary__code {
  font-size: 22px;
  font-weight: 700;
  color: #2563eb;
  letter-spacing: 0.02em;
  line-height: 1.25;
}

.m-summary__sys {
  margin-top: 4px;
  font-size: 13px;
}

.m-summary__sub {
  margin-top: 6px;
  font-size: 14px;
  color: #64748b;
}

.m-summary__loc {
  margin-top: 8px;
  font-size: 14px;
}

.m-form {
  background: #fff;
  border-radius: 12px;
  padding: 12px 16px 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.m-loc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.m-loc-text {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.m-qty-readonly {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.m-qty-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.m-field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.45;
}

.m-branch-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}

.m-readonly-input :deep(.el-input__wrapper) {
  background-color: #f8fafc;
}

.m-actions {
  margin-top: 16px;
  display: flex;
  justify-content: stretch;
  gap: 10px;
}

.m-actions .el-button {
  flex: 1;
}

.m-actions--split .el-button[type='default'] {
  flex: 0 0 auto;
  min-width: 44%;
}

.m-tx-drawer {
  min-height: 120px;
}

.m-tx-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.m-tx-card__head {
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
}

.m-tx-card__head .muted {
  font-weight: 400;
  color: #94a3b8;
  font-size: 12px;
}

.m-tx-line {
  font-size: 13px;
  color: #334155;
  margin-top: 4px;
}

.m-tx-line.muted {
  color: #64748b;
}

.m-tx-balances {
  margin-top: 4px;
}

.m-tx-balance-line {
  padding-left: 0.5em;
  color: #475569;
}

.m-tx-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
}

.m-hint {
  margin-top: 12px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.m-color-card-edit {
  margin-top: 8px;
}

.m-color-card-hint {
  color: #c0392b;
  font-weight: 600;
}

.m-meter-actual-edit {
  margin-bottom: 8px;
}

.m-meter-actual-hint {
  color: #c0392b;
  font-weight: 600;
}

.m-qty-cur {
  margin: 0 0 8px;
  font-size: 14px;
  color: #475569;
}

.m-qty-tip {
  margin: 0 0 12px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.m-qty-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.m-qty-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.m-qty-plain {
  flex: 1;
  min-width: 0;
}

.m-qty-plain :deep(.el-input__wrapper) {
  border-radius: 10px;
}

.m-qty-plain :deep(.el-input__inner) {
  font-size: 16px;
  font-weight: 600;
  text-align: left;
}

.m-qty-step {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.m-qty-step .el-button {
  margin: 0;
  min-width: 52px;
  padding: 6px 8px;
}
</style>
